// --------------------------------------------------------------------------------
import { ReactNode, Component, CSSProperties } from 'react';
import _ from 'lodash';
import { IsNullOrWhiteSpace } from '@Utils/Micro';
import DateRangePicker, { DateRange, RangeType, DateRangePickerProps } from 'rsuite/DateRangePicker';
import { ButtonGroup } from 'rsuite';
import { Formatters, DateFormat } from '@Utils/Formats';
import { Localization } from '@Primitive/Localization/Localization';
import moment from 'moment';
import Tippy from '@tippyjs/react';
import './RsDateRangePicker.less';

import {
    RsDateRangePickerLocalizationTextMapKey,
    GetRsDateRangePickerLocalizationDictionary,
    RsDateRangePickerLocalizationLabelKey
} from '@Primitive/RsDateRangePickerLocalization';

import {
    addDays, addMonths,
    subDays, subHours, subMonths, subYears,
    differenceInDays, isEqual,
    startOfWeek, startOfMonth, startOfYear, startOfDay,
    endOfWeek, endOfMonth, endOfYear, endOfDay,
    isFirstDayOfMonth, isLastDayOfMonth, lastDayOfYear
} from 'date-fns';
// --------------------------------------------------------------------------------
interface RsDateRangePickerProps {
    size?: 'lg' | 'md' | 'sm' | 'xs';
    dontInitRange?: boolean;
    showOneCalendar?: boolean;
    localizationKey?: string;
    transparent?: boolean;
    fullWidth?: boolean;
    style?: CSSProperties;
    subtle?: boolean;
    noCaret?: boolean;
    cleanable?: boolean;
    format?: string;
    disabledWeekends?: boolean;
    disabled?: boolean;
    readonly?: boolean;
    navEnabled?: boolean;
    inMobile?: boolean;
    isoWeek?: boolean;
    ranges?: RangeType[];
    usePredefinedRanges?: boolean;
    hasTime?: boolean;
    placeholder?: string;
    initialDaysDiff?: number;
    initialHoursDiff?: number;
    value: DateRange;
    renderValue?: (value: DateRange, format: string) => string;
    onChange: (value: DateRange) => void;
    onChangeBackwardCompatible?: (start: Date | string, end: Date | string) => void;
}
// --------------------------------------------------------------------------------
interface RsDateRangePickerState {
    value: DateRange;
}
// --------------------------------------------------------------------------------
export default class RsDateRangePicker extends Component<RsDateRangePickerProps, RsDateRangePickerState> {
    constructor(props: RsDateRangePickerProps) {
        super(props);

        let value = props.value;
        if ((value == null ||
            !moment(value[0]).isValid() ||
            !moment(value[1]).isValid()) &&
            !props.dontInitRange) {
            value = [moment().toDate(), moment().toDate()];

            if (_.isNumber(props.initialDaysDiff))
                value[0] = subDays(value[0], props.initialDaysDiff);

            if (_.isNumber(props.initialHoursDiff))
                value[0] = subHours(value[0], props.initialHoursDiff);
        }
        this.state = { value: value };
    }
    // ----------------------------------------------------------------------------
    componentDidUpdate(prevProps: RsDateRangePickerProps) {
        if (this.props.value != null &&
            this.props.value != prevProps.value) {
            if (moment(this.props.value[0]).isValid() &&
                moment(this.props.value[1]).isValid())
                this.setState({ value: this.props.value });
        }
    }
    // ----------------------------------------------------------------------------
    get localizationKey() {
        return this.props.localizationKey;
    }
    // ----------------------------------------------------------------------------
    get hasTime() {
        return this.props.hasTime
            || this.props.format?.indexOf('HH:mm') > -1
            || this.props.format?.indexOf('hh:mm') > -1;
    }
    // ----------------------------------------------------------------------------
    get nonInteractive() {
        return this.props.disabled || this.props.readonly;
    }
    // ----------------------------------------------------------------------------
    getProps = () => {
        let props: DateRangePickerProps = {};
        props.className = 'rs-picker-daterange-primitive';
        props.format = 'yyyy-MM-dd';
        props.character = ' - ';
        props.placement = 'autoVerticalStart';
        props.editable = false;
        props.preventOverflow = true;
        props.showOneCalendar = this.props.showOneCalendar ?? !this.hasTime;
        props.style = this.props.style;
        props.block = this.props.fullWidth;
        props.disabled = this.props.disabled;
        props.readOnly = this.props.readonly;
        props.isoWeek = this.props.isoWeek;
        props.ranges = this.props.ranges;
        props.locale = this.getLocale();
        props.value = this.state.value;
        props.onChange = this.onChange;

        if (this.props.navEnabled) {
            props.className += ' flat';
            if (this.props.fullWidth)
                props.style.width = 'calc(100% - 55px)';
        }

        if (this.props.inMobile)
            props.menuClassName = 'mobile';

        if (this.props.disabledWeekends)
            props.disabledDate = date =>
                date.toString().indexOf('Sat') > -1 ||
                date.toString().indexOf('Sun') > -1;

        if (!IsNullOrWhiteSpace(this.props.format))
            props.format = this.props.format.replace(/Y/g, 'y').replace(/D/g, 'd');

        if (this.props.hasTime &&
            props.format.indexOf('HH:mm') == -1 &&
            props.format.indexOf('h:mm') == -1)
            props.format += ' HH:mm';

        if (!IsNullOrWhiteSpace(this.props.placeholder))
            props.placeholder = this.props.placeholder;

        if (this.props.subtle)
            props.appearance = 'subtle';

        if (this.props.size != null)
            props.size = this.props.size;

        if (this.props.cleanable != null)
            props.cleanable = this.props.cleanable;

        if (this.props.noCaret)
            props.className += ' no-caret';

        if (_.isFunction(this.props.renderValue))
            props.renderValue = this.props.renderValue;

        if (props.ranges == null && this.props.usePredefinedRanges)
            props.ranges = this.getPredefinedRanges();

        if (this.props.transparent)
            props.className += ' transparent';

        return props;
    }
    // ----------------------------------------------------------------------------
    getLocale = () => {
        let defaultLocale = GetRsDateRangePickerLocalizationDictionary();
        if (this.localizationKey == null)
            return defaultLocale;

        let localization = Localization.GetInstance(this.localizationKey);
        if (localization == null)
            return defaultLocale;

        let map = localization.GetTextMap(RsDateRangePickerLocalizationTextMapKey);
        if (map == null)
            return defaultLocale;

        let locale = defaultLocale;
        _.forEach(map, (value, key) => {
            if (!_.isEmpty(value))
                locale[key] = value;
        });
        return locale;
    }
    // ----------------------------------------------------------------------------
    onChange = (value: DateRange) => {
        this.setState((s: RsDateRangePickerState) => {
            s.value = value;

            if (_.isFunction(this.props.onChange))
                this.props.onChange(value);

            if (_.isFunction(this.props.onChangeBackwardCompatible)) {
                if (value == null) {
                    this.props.onChangeBackwardCompatible(null, null);
                } else {
                    let range = Formatters.datetimeRange(
                        value[0],
                        value[1],
                        DateFormat.ISO,
                        this.hasTime,
                        this.hasTime).split(' - ');

                    this.props.onChangeBackwardCompatible(range[0], range[1]);
                }
            }

            return s;
        });
    }
    // ----------------------------------------------------------------------------
    getNavArrow = (direction: 'left' | 'right') => {
        let iconClass: string = null;
        let tooltip: string = null;
        let locale = this.getLocale();

        // decorate element
        switch (direction) {
            case 'left':
                iconClass = 'fas fa-angle-left';
                tooltip = locale == null ? null : locale[RsDateRangePickerLocalizationLabelKey.back];
                break;
            case 'right':
                iconClass = 'fas fa-angle-right';
                tooltip = locale == null ? null : locale[RsDateRangePickerLocalizationLabelKey.forth];
                break;
            default: // when null
                return;
        }

        // button for navigation
        return (
            <Tippy animation={false} placement='bottom' content={tooltip}>
                <div
                    className={iconClass}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0 8px',
                        backgroundColor: this.props.transparent ? 'transparent' : 'white',
                        color: this.props.style?.color ?? '#8e8e93',
                        fontSize: this.props.style?.fontSize ?? 16,
                        cursor: this.nonInteractive ? 'not-allowed' : 'pointer'
                    }}
                    onClick={() => {
                        if (this.nonInteractive || this.state.value == null)
                            return;

                        let value = _.clone(this.state.value);

                        // shift year
                        if (isEqual(startOfYear(value[0]), value[0]) &&
                            isEqual(lastDayOfYear(value[1]), value[1])) {
                            if (direction == 'left') {
                                value[0] = new Date(value[0].getFullYear() - 1, 0, 1);
                                value[1] = new Date(value[1].getFullYear(), 0, 0);
                            }
                            else if (direction == 'right') {
                                value[0] = new Date(value[0].getFullYear() + 1, 0, 1);
                                value[1] = new Date(value[1].getFullYear() + 2, 0, 0);
                            }
                        }
                        // shift month 
                        else if (isFirstDayOfMonth(value[0]) && isLastDayOfMonth(value[1])) {
                            if (direction == 'left') {
                                value[0] = startOfMonth(addMonths(value[0], -1));
                                value[1] = endOfMonth(addMonths(value[1], -1));
                            }
                            else if (direction == 'right') {
                                value[0] = startOfMonth(addMonths(value[0], 1));
                                value[1] = endOfMonth(addMonths(value[1], 1));
                            }
                        }
                        // shift days 
                        else {
                            let daysDiff = differenceInDays(value[0], value[1]);
                            if (daysDiff == 0) {
                                daysDiff = 1;
                            }
                            daysDiff = Math.abs(daysDiff);

                            if (direction == 'left') {
                                value[0] = subDays(value[0], daysDiff);
                                value[1] = subDays(value[1], daysDiff);
                            }
                            else if (direction == 'right') {
                                value[0] = addDays(value[0], daysDiff);
                                value[1] = addDays(value[1], daysDiff)
                            }
                        }
                        this.onChange(value);
                    }}
                />
            </Tippy>
        );
    }
    // ----------------------------------------------------------------------------
    getPredefinedRanges = () => {
        let locale = this.getLocale();
        if (locale != null) {
            let currentDate = new Date();
            let todayStart = startOfDay(currentDate);
            let todayEnd = endOfDay(currentDate);
            let valueToday = [todayStart, todayEnd];
            let valueYesterday = [subDays(todayStart, 1), subDays(todayEnd, 1)];
            let valueThisWeek = [startOfWeek(currentDate), endOfWeek(currentDate)];
            let valueLast7Days = [subDays(todayStart, 6), todayEnd];
            let valueLast30Days = [subDays(todayStart, 29), todayEnd];
            let valueThisMonth = [startOfMonth(currentDate), todayEnd];
            let valueLastMonth = [startOfMonth(subMonths(currentDate, 1)), endOfMonth(subMonths(currentDate, 1))];
            let valueThisYear = [startOfYear(currentDate), todayEnd];
            let valueLastYear = [startOfYear(subYears(currentDate, 1)), endOfYear(subYears(currentDate, 1))];
            return [
                { placement: 'left', label: locale[RsDateRangePickerLocalizationLabelKey.today], value: valueToday },
                { placement: 'left', label: locale[RsDateRangePickerLocalizationLabelKey.yesterday], value: valueYesterday },
                { placement: 'left', label: locale[RsDateRangePickerLocalizationLabelKey.thisWeek], value: valueThisWeek },
                { placement: 'left', label: locale[RsDateRangePickerLocalizationLabelKey.last7Days], value: valueLast7Days },
                { placement: 'left', label: locale[RsDateRangePickerLocalizationLabelKey.last30Days], value: valueLast30Days },
                { placement: 'left', label: locale[RsDateRangePickerLocalizationLabelKey.thisMonth], value: valueThisMonth },
                { placement: 'left', label: locale[RsDateRangePickerLocalizationLabelKey.lastMonth], value: valueLastMonth },
                { placement: 'left', label: locale[RsDateRangePickerLocalizationLabelKey.thisYear], value: valueThisYear },
                { placement: 'left', label: locale[RsDateRangePickerLocalizationLabelKey.lastYear], value: valueLastYear }
            ] as RangeType[];
        }
    }
    // ----------------------------------------------------------------------------
    render() {
        let dateRangePicker = <DateRangePicker as={'div'} {...this.getProps()} />;
        if (this.props.navEnabled)
            return <ButtonGroup style={{
                display: 'flex',
                alignItems: 'stretch',
                justifyContent: 'center'
            }}>
                {this.getNavArrow('left')}
                {dateRangePicker}
                {this.getNavArrow('right')}
            </ButtonGroup>;
        else
            return dateRangePicker;
    }
    // ----------------------------------------------------------------------------    
}
// --------------------------------------------------------------------------------